// Input Flows data + mutations (PRD §4.4 / WP-09).
// Read from tenants/{t}/inputFlows; CRUD & reset via owner/admin/agent callables.
import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from './firebase';
import { useTenant } from './tenant';
import { call } from './callable';

export interface FlowQuestion {
  id: string;
  prompt: string;
  fieldKey: string;
}

export interface InputFlow {
  id: string;
  name: string;
  trigger: string;
  enabled: boolean;
  questions: FlowQuestion[];
  completion?: string | null;
  googleSheetId?: string | null;
  googleAccountId?: string | null;
  completions: number;
  createdAt: number;
  updatedAt: number;
}

export interface FlowInput {
  id?: string;
  name: string;
  trigger: string;
  enabled: boolean;
  questions: FlowQuestion[];
  completion?: string | null;
  googleSheetId?: string | null;
  googleAccountId?: string | null;
}

export function useInputFlows(): { flows: InputFlow[]; loading: boolean } {
  const { tenantId } = useTenant();
  const [flows, setFlows] = useState<InputFlow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) {
      setFlows([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, `tenants/${tenantId}/inputFlows`),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(
      q,
      (snap) => {
        setFlows(
          snap.docs.map((d) => {
            const f = d.data() as Partial<InputFlow>;
            return {
              id: d.id,
              name: f.name ?? '',
              trigger: f.trigger ?? '',
              enabled: f.enabled ?? false,
              questions: f.questions ?? [],
              completion: f.completion ?? null,
              googleSheetId: f.googleSheetId ?? null,
              googleAccountId: f.googleAccountId ?? null,
              completions: f.completions ?? 0,
              createdAt: f.createdAt ?? 0,
              updatedAt: f.updatedAt ?? 0,
            };
          })
        );
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [tenantId]);

  return { flows, loading };
}

// ── Callables ────────────────────────────────────────────────────────────────

export const inputFlowSave = call<FlowInput, { ok: boolean; id: string }>('inputFlowSave');
export const inputFlowDelete = call<{ id: string }, { ok: boolean }>('inputFlowDelete');
export const inputFlowList = call<{}, { ok: boolean; flows: InputFlow[] }>('inputFlowList');
export const inputFlowGet = call<{ id: string }, { ok: boolean; flow: InputFlow }>('inputFlowGet');
export const inputFlowReset = call<
  { conversationId?: string; subscriberId?: string },
  { ok: boolean; reset: number }
>('inputFlowReset');

// Helper aliases
export const saveInputFlow = async (input: FlowInput) => (await inputFlowSave(input)).data;
export const deleteInputFlow = async (id: string) => (await inputFlowDelete({ id })).data;
export const resetInputFlow = async (params: { conversationId?: string; subscriberId?: string }) =>
  (await inputFlowReset(params)).data;
